import { useEffect, useRef, useState } from 'react';
import { PHOTO_KIND_LABELS, api, photoUrl, type Photo } from '../api.ts';
import { Icon } from './Icon.tsx';

/**
 * Галерея карточки.
 *
 * Макет 2 предлагает точки-индикаторы, и первая версия их и делала. На
 * телефоне выяснилось, что этого мало: точки читаются как индикатор, а не
 * как кнопка, и второй снимок открыть было нечем. Поэтому здесь три способа
 * сразу — стрелки под большой палец, счётчик «2 из 3», который сообщает,
 * что снимков вообще больше одного, и лента миниатюр, где видно все.
 *
 * Фото этикетки сюда не попадает — оно показывается отдельным блоком.
 */
export function Gallery({ photos, onChanged }: { photos: Photo[]; onChanged: () => void }) {
  const [index, setIndex] = useState(0);
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState(false);

  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  // preventDefault на pointerup клик не отменяет, поэтому листание
  // помечается флагом — иначе свайп заодно открывал бы полный экран.
  const swiped = useRef(false);

  useEffect(() => {
    if (index >= photos.length) setIndex(0);
  }, [photos.length, index]);

  if (photos.length === 0) {
    return (
      <div className="well" style={{ height: 180 }}>
        <Icon name="photo" className="icon icon-lg" />
      </div>
    );
  }

  const position = Math.min(index, photos.length - 1);
  const current = photos[position]!;
  const many = photos.length > 1;

  const go = (step: number) =>
    setIndex((i) => (i + step + photos.length) % photos.length);

  async function setCover() {
    setBusy(true);
    await api.setCover(current.id);
    setBusy(false);
    onChanged();
  }

  async function remove() {
    setBusy(true);
    await api.deletePhoto(current.id);
    setBusy(false);
    onChanged();
  }

  return (
    <>
      <div className="gallery">
        <button
          type="button"
          className="gallery-frame"
          aria-label="Открыть во весь экран"
          onClick={() => {
            if (swiped.current) {
              swiped.current = false;
              return;
            }
            setFull(true);
          }}
          onPointerDown={(e) => {
            swipeStart.current = { x: e.clientX, y: e.clientY };
            // Без захвата указателя pointerup при быстром движении
            // приходит уже не на этот элемент.
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerCancel={() => {
            swipeStart.current = null;
          }}
          onPointerUp={(e) => {
            const start = swipeStart.current;
            swipeStart.current = null;
            if (!start) return;
            const dx = e.clientX - start.x;
            const dy = e.clientY - start.y;
            // Порог по горизонтали и требование, чтобы движение было
            // именно горизонтальным: иначе прокрутка страницы пальцем
            // будет через раз перелистывать снимки.
            if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
            swiped.current = true;
            go(dx < 0 ? 1 : -1);
          }}
        >
          <img
            className="gallery-image"
            src={photoUrl(current.id)}
            alt={current.caption ?? PHOTO_KIND_LABELS[current.kind]}
          />
        </button>

        {many && (
          <>
            <button
              type="button"
              className="gallery-arrow gallery-arrow--prev"
              aria-label="Предыдущий снимок"
              onClick={() => go(-1)}
            >
              <Icon name="chevron" className="icon" />
            </button>
            <button
              type="button"
              className="gallery-arrow gallery-arrow--next"
              aria-label="Следующий снимок"
              onClick={() => go(1)}
            >
              <Icon name="chevron" className="icon" />
            </button>
            <div className="gallery-counter">
              {position + 1} из {photos.length}
            </div>
          </>
        )}
      </div>

      {many && (
        <div className="strip gallery-strip">
          {photos.map((photo, i) => (
            <button
              key={photo.id}
              type="button"
              className={i === position ? 'gallery-thumb is-active' : 'gallery-thumb'}
              aria-label={`Снимок ${i + 1}`}
              aria-current={i === position}
              onClick={() => setIndex(i)}
            >
              <img src={photoUrl(photo.id, true)} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      <div className="chips" style={{ marginTop: 'var(--space-2)' }}>
        <span className="chip chip--plain">{PHOTO_KIND_LABELS[current.kind]}</span>
        {current.is_cover ? (
          <span className="chip chip--ok">обложка</span>
        ) : (
          <button type="button" className="chip chip--plain" disabled={busy} onClick={setCover}>
            сделать обложкой
          </button>
        )}
        <button type="button" className="chip chip--plain" disabled={busy} onClick={remove}>
          удалить снимок
        </button>
      </div>

      {full && (
        <div className="lightbox" role="dialog" aria-label="Просмотр снимка">
          <button
            type="button"
            className="lightbox-backdrop"
            aria-label="Закрыть"
            onClick={() => setFull(false)}
          />
          <img className="lightbox-image" src={photoUrl(current.id)} alt="" />

          {many && (
            <>
              <button
                type="button"
                className="gallery-arrow gallery-arrow--prev"
                aria-label="Предыдущий снимок"
                onClick={() => go(-1)}
              >
                <Icon name="chevron" className="icon" />
              </button>
              <button
                type="button"
                className="gallery-arrow gallery-arrow--next"
                aria-label="Следующий снимок"
                onClick={() => go(1)}
              >
                <Icon name="chevron" className="icon" />
              </button>
              <div className="gallery-counter">
                {position + 1} из {photos.length}
              </div>
            </>
          )}

          <button
            type="button"
            className="lightbox-close"
            aria-label="Закрыть"
            onClick={() => setFull(false)}
          >
            <Icon name="x" className="icon" />
          </button>
        </div>
      )}
    </>
  );
}
